import React, { useEffect, useState, useMemo, useCallback, useRef } from "react";
import DataTable from "react-data-table-component";
import {
    AiOutlineEdit,
    AiOutlineDelete,
    AiOutlineSync,
    AiOutlineCheckCircle,
    AiOutlineClose,
    AiOutlineSearch,
    AiOutlineUserAdd,
} from "react-icons/ai";
import authService from "../../services/authService";

// ===== REAL API SECTION =====
import { API_CONFIG } from '../../config/env';
const API_BASE_URL = `${API_CONFIG.BASE_URL}/admin`;

async function apiFetchEmployees() {
    console.log('Making API request to:', `${API_BASE_URL}/employees/`);
    
    const response = await authService.adminApiCall(`${API_BASE_URL}/employees/`);
    
    console.log('API Response status:', response.status);
    
    if (!response.ok) {
        const errorText = await response.text();
        console.error('API Error response:', errorText);
        throw new Error(`Failed to fetch employees: ${response.status} - ${errorText}`);
    }
    
    const data = await response.json();
    console.log('API Success response:', data);
    return data.data || [];
}

async function apiCreateEmployee(employee) {
    const response = await authService.adminApiCall(`${API_BASE_URL}/employees/create/`, {
        method: 'POST',
        body: JSON.stringify(employee)
    });
    
    if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Failed to create employee' }));
        throw new Error(errorData.error || errorData.details || 'Failed to create employee');
    }
    
    const data = await response.json();
    console.log('Employee created:', data.data);
    return data.data;
}

async function apiEditEmployee(employee) {
    const response = await authService.adminApiCall(`${API_BASE_URL}/employees/${employee.id}/`, {
        method: 'PUT',
        body: JSON.stringify(employee)
    });
    
    if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Failed to update employee' }));
        throw new Error(errorData.error || errorData.details || 'Failed to update employee');
    }
    
    const data = await response.json();
    console.log('Employee updated:', data.data);
    return data.data;
}

async function apiDeleteEmployee(id) {
    const response = await authService.adminApiCall(`${API_BASE_URL}/employees/${id}/delete/`, {
        method: 'DELETE'
    });
    
    if (!response.ok) {
        throw new Error('Failed to delete employee');
    }
    
    return true;
}

async function apiChangeStatus(id) {
    const response = await authService.adminApiCall(`${API_BASE_URL}/employees/${id}/toggle-status/`, {
        method: 'POST'
    });
    
    if (!response.ok) {
        throw new Error('Failed to change employee status');
    }
    
    return true;
}
// ===========================================

export default function Employees() {
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(false);

    // Pagination / Controls
    const [perPage, setPerPage] = useState(10);
    const [currentPage, setCurrentPage] = useState(1);

    // Search query
    const [search, setSearch] = useState("");

    // Add/Edit modal states
    const [showAddModal, setShowAddModal] = useState(false);
    const [editRow, setEditRow] = useState(null);
    const [formVals, setFormVals] = useState({
        name: "",
        employee_id: "",
        email: "",
        mobile: "",
        joined_date: "",
        resigned_date: "",
        still_working: true
    });
    const [formErrs, setFormErrs] = useState({});
    const [formTouched, setFormTouched] = useState({});

    // Delete modal
    const [deleteRow, setDeleteRow] = useState(null);

    // Success message modal
    const [successMsg, setSuccessMsg] = useState("");

    // Fetch employees on mount
    useEffect(() => {
        async function fetchEmployees() {
            setLoading(true);
            try {
                const token = localStorage.getItem('adminAuthToken');
                if (!token) {
                    console.error('No admin token found');
                    setData([]);
                    setLoading(false);
                    return;
                }
                
                console.log('Fetching employees with token:', token.substring(0, 20) + '...');
                const list = await apiFetchEmployees();
                setData(list);
                console.log('Employees fetched successfully:', list.length);
            } catch (error) {
                console.error('Error fetching employees:', error);
                setData([]);
            }
            setLoading(false);
        }
        fetchEmployees();
    }, []);

    // Filter data by name
    const filteredData = useMemo(() => {
        if (!search.trim()) return data;
        const q = search.trim().toLowerCase();
        const filtered = data.filter(
            d => {
                const name = (d.name || '').toLowerCase();
                return name.includes(q);
            }
        );
        return filtered;
    }, [data, search]);

    // Columns configuration
    const columns = useMemo(() => [
        {
            name: "S.No",
            width: "80px",
            minWidth: "56px",
            cell: (_row, idx) => (currentPage - 1) * perPage + idx + 1,
            style: { fontWeight: 600, color: "#4338ca", textAlign: "center", whiteSpace: "nowrap" },
            sortable: false,
            center: true,
        },
        {
            name: "Name",
            selector: row => row.name,
            sortable: true,
            minWidth: "150px",
            maxWidth: "250px",
            style: { fontWeight: 600, whiteSpace: "nowrap", textOverflow: "ellipsis", overflow: "hidden" },
            cell: row => <span title={row.name}>{row.name || 'N/A'}</span>,
        },
        {
            name: "Employee ID",
            selector: row => row.employee_id,
            sortable: true,
            minWidth: "120px",
            maxWidth: "200px",
            style: { fontWeight: 500, color: "#116", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" },
            cell: row => <span title={row.employee_id}>{row.employee_id || 'N/A'}</span>,
        },
        {
            name: "Mail ID",
            selector: row => row.email,
            sortable: true,
            minWidth: "200px",
            maxWidth: "300px",
            style: { fontWeight: 500, color: "#116", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" },
            cell: row => <span title={row.email} style={{ display: "block", textAlign: "left" }}>{row.email || 'N/A'}</span>,
        },
        {
            name: "Mobile",
            selector: row => row.mobile,
            sortable: true,
            minWidth: "130px",
            maxWidth: "200px",
            style: { fontWeight: 500, color: "#164d26", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" },
            cell: row => <span title={row.mobile}>{row.mobile || 'N/A'}</span>,
            center: true,
        },
        {
            name: "Joined Date",
            selector: row => row.joined_date,
            sortable: true,
            minWidth: "130px",
            maxWidth: "200px",
            style: { fontWeight: 500, whiteSpace: "nowrap" },
            cell: row => <span>{row.joined_date ? new Date(row.joined_date).toLocaleDateString() : 'N/A'}</span>,
            center: true,
        },
        {
            name: "Resigned Date",
            selector: row => row.resigned_date,
            sortable: true,
            minWidth: "130px",
            maxWidth: "200px",
            style: { fontWeight: 500, whiteSpace: "nowrap" },
            cell: row => <span>{row.resigned_date ? new Date(row.resigned_date).toLocaleDateString() : '-'}</span>,
            center: true,
        },
        {
            name: "Status",
            selector: row => row.status,
            minWidth: "100px",
            maxWidth: "150px",
            style: { textAlign: "center", whiteSpace: "nowrap" },
            cell: row => (
                <span style={{
                    borderRadius: "14px",
                    padding: "5px 15px",
                    fontWeight: 700,
                    color: "#fff",
                    fontSize: 14,
                    background: row.status === 'working' ? "#21bb55" : "#dc2626",
                    boxShadow: row.status === 'working' ? "0 2px 8px #21bb551a" : "0 2px 9px #dc262611",
                    letterSpacing: ".02em",
                    userSelect: "none",
                    display: "inline-block",
                    minWidth: 70
                }}>
                    {row.status === 'working' ? "Working" : "Resigned"}
                </span>
            ),
            center: true,
            sortable: false,
        },
        {
            name: "Actions",
            minWidth: "130px",
            maxWidth: "150px",
            style: { whiteSpace: "nowrap" },
            cell: row => (
                <div style={{
                    display: "flex",
                    justifyContent: "center",
                    gap: 10,
                    whiteSpace: "nowrap",
                }}>
                    <button
                        onClick={() => openEdit(row)}
                        style={iconBtn('#3193eb')}
                        aria-label="Edit"
                        title="Edit"
                    >
                        <AiOutlineEdit size={18} />
                    </button>
                    <button
                        onClick={() => openDelete(row)}
                        style={iconBtn('#dc2626')}
                        aria-label="Delete"
                        title="Delete"
                    >
                        <AiOutlineDelete size={18} />
                    </button>
                    <button
                        onClick={() => handleChangeStatus(row)}
                        style={iconBtn('#2979ff')}
                        aria-label="Change Status"
                        title="Change Status"
                    >
                        <AiOutlineSync size={18} />
                    </button>
                </div>
            ),
            ignoreRowClick: true,
            center: true,
            sortable: false,
        }
    ], [currentPage, perPage]);

    // Pagination handlers
    function handlePageChange(page) { setCurrentPage(page); }
    function handlePerRowsChange(newPerPage, page) { setPerPage(newPerPage); setCurrentPage(page); }

    // Open Add Modal
    function openAddModal() {
        setShowAddModal(true);
        setFormVals({
            name: "",
            employee_id: "",
            email: "",
            mobile: "",
            joined_date: "",
            resigned_date: "",
            still_working: true
        });
        setFormErrs({});
        setFormTouched({});
    }

    // Edit modal open
    function openEdit(row) {
        setEditRow(row);
        setShowAddModal(true);
        setFormVals({
            name: row.name || '',
            employee_id: row.employee_id || '',
            email: row.email || '',
            mobile: row.mobile || '',
            joined_date: row.joined_date || '',
            resigned_date: row.resigned_date || '',
            still_working: !row.resigned_date
        });
        setFormErrs({});
        setFormTouched({});
    }

    // Validate form fields
    function validate(vals) {
        const errs = {};
        if (!vals.name.trim()) errs.name = "Name is required";
        if (!vals.employee_id.trim()) errs.employee_id = "Employee ID is required";
        if (!vals.email.trim()) errs.email = "Email is required";
        else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(vals.email.trim())) errs.email = "Invalid email";
        if (!vals.mobile.trim()) errs.mobile = "Mobile is required";
        else if (!/^[0-9]+$/.test(vals.mobile.trim())) errs.mobile = "Numbers only";
        if (!vals.joined_date) errs.joined_date = "Joined date is required";
        if (!vals.still_working && !vals.resigned_date) {
            errs.resigned_date = "Resigned date is required when not still working";
        }
        return errs;
    }

    function isFormValid() {
        const errs = validate(formVals);
        return Object.keys(errs).length === 0;
    }

    function handleInputChange(e) {
        const { name, value, type, checked } = e.target;
        const val = type === 'checkbox' ? checked : value;
        
        setFormVals(v => {
            const newVals = { ...v, [name]: val };
            // If still_working is checked, clear resigned_date
            if (name === 'still_working' && checked) {
                newVals.resigned_date = '';
            }
            return newVals;
        });
        setFormTouched(t => ({ ...t, [name]: true }));
        setFormErrs(validate({ ...formVals, [name]: val }));
    }

    async function handleSave(e) {
        e.preventDefault();
        const errs = validate(formVals);
        setFormErrs(errs);
        setFormTouched({
            name: true,
            employee_id: true,
            email: true,
            mobile: true,
            joined_date: true,
            resigned_date: true
        });
        if (Object.keys(errs).length) return;

        setLoading(true);
        try {
            const employeeData = {
                name: formVals.name.trim(),
                employee_id: formVals.employee_id.trim(),
                email: formVals.email.trim(),
                mobile: formVals.mobile.trim(),
                joined_date: formVals.joined_date,
                resigned_date: formVals.still_working ? null : (formVals.resigned_date || null),
                still_working: formVals.still_working
            };

            if (editRow) {
                await apiEditEmployee({ ...employeeData, id: editRow.id });
                setSuccessMsg("Employee details edited successfully");
            } else {
                const created = await apiCreateEmployee(employeeData);
                console.log('Employee created:', created);
                setSuccessMsg("Employee added successfully");
            }
            
            // Refresh data
            const list = await apiFetchEmployees();
            setData(list);
        } catch (error) {
            setSuccessMsg("Error: " + error.message);
        }
        setLoading(false);

        setShowAddModal(false);
        setEditRow(null);
        setFormVals({
            name: "",
            employee_id: "",
            email: "",
            mobile: "",
            joined_date: "",
            resigned_date: "",
            still_working: true
        });
    }

    function handleCancel() {
        setShowAddModal(false);
        setEditRow(null);
        setFormVals({
            name: "",
            employee_id: "",
            email: "",
            mobile: "",
            joined_date: "",
            resigned_date: "",
            still_working: true
        });
        setFormErrs({});
        setFormTouched({});
    }

    // Delete modal open
    function openDelete(row) { setDeleteRow(row); }

    async function handleDeleteYes() {
        setLoading(true);
        try {
            await apiDeleteEmployee(deleteRow.id);
            const list = await apiFetchEmployees();
            setData(list);
            setSuccessMsg("Employee deleted successfully");
        } catch (error) {
            setSuccessMsg("Error: " + error.message);
        }
        setLoading(false);

        setDeleteRow(null);
    }

    // Status toggle
    async function handleChangeStatus(row) {
        setLoading(true);
        try {
            await apiChangeStatus(row.id);
            const list = await apiFetchEmployees();
            setData(list);
        } catch (error) {
            setSuccessMsg("Error: " + error.message);
        }
        setLoading(false);
    }

    function closeSuccess() { setSuccessMsg(""); }

    // Custom styles
    const customStyles = {
        table: { style: { width: "100%" } },
        rows: { style: { fontSize: 15, background: "#f8fafc", whiteSpace: "nowrap" } },
        headRow: { style: { background: "#eceffd", color: "#433ca", fontWeight: 700, fontSize: 15 } },
        headCells: { style: { borderBottom: "1.6px solid #a5a6ff", padding: "6px 9px" } },
        cells: { style: { padding: "6px 9px" } },
        pagination: { style: { borderTop: "1px solid #aab6fe", color: "#433ca", fontWeight: 600 } }
    };

    // Search input ref
    const searchInputRef = useRef(null);

    // Focus management for search input
    const handleSearchFocus = useCallback(() => {
        if (searchInputRef.current) {
            searchInputRef.current.focus();
        }
    }, []);

    // Search Input Bar Component
    const SearchBar = useCallback(() => {
        return (
            <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                <div 
                    style={{
                        display: "flex",
                        alignItems: "center",
                        borderRadius: 6,
                        border: "1.5px solid #aab6fe",
                        background: "#f0f7ff",
                        padding: "0 10px",
                        maxWidth: 280,
                        width: "100%"
                    }}
                    onClick={handleSearchFocus}
                >
                    <input
                        ref={searchInputRef}
                        type="text"
                        placeholder="Search employee name"
                        value={search}
                        onChange={e => setSearch(e.target.value)}
                        style={{
                            border: "none",
                            flexGrow: 1,
                            height: 30,
                            fontSize: 14,
                            outline: "none",
                            background: "transparent"
                        }}
                        aria-label="Search employees"
                        autoComplete="off"
                    />
                    {search.trim() ? (
                        <button
                            type="button"
                            onClick={() => {
                                setSearch("");
                                setTimeout(handleSearchFocus, 0);
                            }}
                            style={{
                                background: "none",
                                border: "none",
                                cursor: "pointer",
                                padding: 0,
                                display: "flex",
                                alignItems: "center"
                            }}
                            title="Clear search"
                        >
                            <AiOutlineClose size={16} style={{ color: "#666" }} />
                        </button>
                    ) : (
                        <AiOutlineSearch size={20} style={{ color: "#2979ff" }} />
                    )}
                </div>
                {search.trim() && (
                    <div style={{
                        fontSize: 12,
                        color: "#666",
                        background: "#f0f0f0",
                        padding: "4px 8px",
                        borderRadius: 4,
                        whiteSpace: "nowrap"
                    }}>
                        {filteredData.length} result{filteredData.length !== 1 ? 's' : ''}
                    </div>
                )}
            </div>
        );
    }, [search, filteredData.length]);

    const [isMobile, setIsMobile] = React.useState(window.innerWidth < 768);
    
    React.useEffect(() => {
        const handleResize = () => setIsMobile(window.innerWidth < 768);
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    return (
        <section style={{ padding: isMobile ? 10 : 20, width: "100%", maxWidth: "100%" }}>
            <div style={{ 
                display: "flex", 
                justifyContent: "space-between", 
                alignItems: isMobile ? "flex-start" : "center", 
                flexWrap: "wrap", 
                marginBottom: 20,
                gap: 15,
            }}>
                <h1 style={{
                    display: "flex",
                    alignItems: "center", 
                    gap: 13,
                    marginBottom: 0,
                    fontSize: isMobile ? 24 : 38,
                    marginRight: 10,
                    color: "#215ea7",
                    verticalAlign: "middle",
                    filter: "drop-shadow(0px 1px 4px #b5beff77)",
                    flex: isMobile ? "1 1 100%" : "0 1 auto",
                }}>
                    <AiOutlineUserAdd size={isMobile ? 24 : 32} color="#215ea7" />
                    <span style={{ whiteSpace: isMobile ? "normal" : "nowrap" }}>
                        {isMobile ? "Employees" : "Employee Management"}
                    </span>
                </h1>
                <div style={{ 
                    width: isMobile ? "100%" : "auto",
                    order: isMobile ? -1 : 0,
                }}>
                    <SearchBar key="search-bar" />
                </div>
            </div>

            <div style={{ 
                marginBottom: 20, 
                display: "flex", 
                justifyContent: "flex-end",
                width: "100%",
            }}>
                <button
                    onClick={openAddModal}
                    style={{
                        padding: isMobile ? "8px 16px" : "10px 20px",
                        background: "#215ea7",
                        color: "#fff",
                        border: "none",
                        borderRadius: 7,
                        fontWeight: 700,
                        fontSize: isMobile ? 14 : 15,
                        cursor: "pointer",
                        display: "flex",
                        alignItems: "center",
                        gap: 8,
                        width: isMobile ? "100%" : "auto",
                        justifyContent: "center",
                    }}
                >
                    <AiOutlineUserAdd size={isMobile ? 16 : 18} />
                    <span>{isMobile ? "Add" : "Add Employee"}</span>
                </button>
            </div>

            <div className="table-responsive-wrapper" style={{ 
                width: "100%", 
                overflowX: "auto",
                WebkitOverflowScrolling: "touch",
            }}>
                <DataTable
                    columns={columns}
                    data={filteredData}
                    pagination
                    paginationPerPage={perPage}
                    paginationRowsPerPageOptions={[10, 20, 30, 40, 50]}
                    onChangeRowsPerPage={handlePerRowsChange}
                    onChangePage={handlePageChange}
                    paginationDefaultPage={currentPage}
                    highlightOnHover
                    striped
                    responsive
                    persistTableHead
                    progressPending={loading}
                    customStyles={customStyles}
                    noHeader
                />
            </div>

            {/* Add/Edit Dialog */}
            {showAddModal && (
                <Modal onClose={handleCancel} title={editRow ? "Edit Employee" : "Add Employee"}>
                    <form onSubmit={handleSave} style={{ display: "flex", flexDirection: "column", gap: 14 }}>
                        <div>
                            <label style={labelStyle}>
                                Employee Name
                                <span style={{ color: "#dd362b" }}> *</span>
                            </label>
                            <input
                                name="name"
                                type="text"
                                value={formVals.name}
                                onChange={handleInputChange}
                                autoFocus
                                style={inputStyle}
                                placeholder="Enter employee name"
                                maxLength={200}
                                required
                            />
                            {formTouched.name && formErrs.name && (
                                <span style={errStyle}>{formErrs.name}</span>
                            )}
                        </div>

                        <div>
                            <label style={labelStyle}>
                                Employee ID
                                <span style={{ color: "#dd362b" }}> *</span>
                            </label>
                            <input
                                name="employee_id"
                                type="text"
                                value={formVals.employee_id}
                                onChange={handleInputChange}
                                style={inputStyle}
                                placeholder="Enter employee ID"
                                maxLength={50}
                                required
                            />
                            {formTouched.employee_id && formErrs.employee_id && (
                                <span style={errStyle}>{formErrs.employee_id}</span>
                            )}
                        </div>

                        <div>
                            <label style={labelStyle}>
                                Mail ID
                                <span style={{ color: "#dd362b" }}> *</span>
                            </label>
                            <input
                                name="email"
                                type="email"
                                value={formVals.email}
                                onChange={handleInputChange}
                                style={inputStyle}
                                placeholder="Enter email"
                                maxLength={254}
                                required
                            />
                            {formTouched.email && formErrs.email && (
                                <span style={errStyle}>{formErrs.email}</span>
                            )}
                        </div>

                        <div>
                            <label style={labelStyle}>
                                Mobile
                                <span style={{ color: "#dd362b" }}> *</span>
                            </label>
                            <input
                                name="mobile"
                                type="text"
                                value={formVals.mobile}
                                onChange={handleInputChange}
                                style={inputStyle}
                                placeholder="Enter mobile number"
                                maxLength={15}
                                required
                            />
                            {formTouched.mobile && formErrs.mobile && (
                                <span style={errStyle}>{formErrs.mobile}</span>
                            )}
                        </div>

                        <div>
                            <label style={labelStyle}>
                                Joined Date
                                <span style={{ color: "#dd362b" }}> *</span>
                            </label>
                            <input
                                name="joined_date"
                                type="date"
                                value={formVals.joined_date}
                                onChange={handleInputChange}
                                style={inputStyle}
                                required
                            />
                            {formTouched.joined_date && formErrs.joined_date && (
                                <span style={errStyle}>{formErrs.joined_date}</span>
                            )}
                        </div>

                        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                            <input
                                name="still_working"
                                type="checkbox"
                                checked={formVals.still_working}
                                onChange={handleInputChange}
                                style={{ width: 18, height: 18, cursor: "pointer" }}
                            />
                            <label style={{ ...labelStyle, margin: 0, cursor: "pointer" }}>
                                Still Working
                            </label>
                        </div>

                        {!formVals.still_working && (
                            <div>
                                <label style={labelStyle}>
                                    Resigned Date
                                </label>
                                <input
                                    name="resigned_date"
                                    type="date"
                                    value={formVals.resigned_date}
                                    onChange={handleInputChange}
                                    style={inputStyle}
                                    min={formVals.joined_date}
                                />
                                {formTouched.resigned_date && formErrs.resigned_date && (
                                    <span style={errStyle}>{formErrs.resigned_date}</span>
                                )}
                            </div>
                        )}

                        <div style={{ display: "flex", justifyContent: "flex-end", gap: 14 }}>
                            <button type="button" onClick={handleCancel} style={modalBtn("#cad2ff", "#234b8a")}>Cancel</button>
                            <button
                                type="submit"
                                style={{
                                    ...modalBtn("#215ea7", "#fff"),
                                    ...(!isFormValid() ? {
                                        background: "#ccc",
                                        color: "#666",
                                        cursor: "not-allowed",
                                        opacity: 0.6
                                    } : {})
                                }}
                                disabled={!isFormValid()}
                            >
                                Save
                            </button>
                        </div>
                    </form>
                </Modal>
            )}

            {/* Delete Dialog */}
            {deleteRow && (
                <Modal onClose={() => setDeleteRow(null)} title="Delete Employee">
                    <div style={{ fontWeight: "600", fontSize: 18, marginBottom: 18 }}>
                        Are you sure you want to delete <b>{deleteRow.name}</b>?
                    </div>
                    <div style={{ display: "flex", justifyContent: "flex-end", gap: 16 }}>
                        <button onClick={() => setDeleteRow(null)} style={modalBtn("#cad2ff", "#234b8a")}>No</button>
                        <button onClick={handleDeleteYes} style={modalBtn("#dd362b", "#fff")}>Yes</button>
                    </div>
                </Modal>
            )}

            {/* Success Modal */}
            {successMsg && (
                <SuccessModal message={successMsg} onClose={closeSuccess} />
            )}

            {/* Responsive fix styles */}
            <style>
                {`
          @media (max-width: 991px) {
            .rdt_TableCol, .rdt_TableCell {
              padding-left: 6px !important;
              padding-right: 6px !important;
              font-size: 13px;
            }
            .rdt_Table {
              min-width: 600px;
            }
          }
          @media (max-width: 767px) {
            .rdt_TableCol, .rdt_TableCell {
              padding-left: 4px !important;
              padding-right: 4px !important;
              font-size: 12px;
            }
            .rdt_Table {
              min-width: 500px;
            }
          }
        `}
            </style>
        </section>
    );
}

// == Helpers & Styles ==

function iconBtn(bg) {
    return {
        background: bg,
        color: "#fff",
        border: "none",
        borderRadius: 7,
        padding: 6,
        margin: 0,
        cursor: "pointer",
        boxShadow: bg === "#2979ff" ? "0 2px 5px #2979ff33" : undefined,
        width: 34,
        height: 34,
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        transition: "background-color 0.2s ease"
    };
}

function modalBtn(bg, color, px = 16, py = 8) {
    return {
        padding: `${py}px ${px}px`,
        background: bg,
        color: color,
        border: "none",
        borderRadius: 7,
        fontWeight: 700,
        fontSize: 15,
        cursor: "pointer",
        outline: "none",
    };
}

const labelStyle = { color: "#215ea7", fontWeight: 700, fontSize: 15, marginBottom: 6, display: "block" };

const inputStyle = {
    padding: 10,
    fontSize: 16,
    borderRadius: 7,
    border: "1.6px solid #aec0fa",
    outline: "none",
    width: "100%",
    fontWeight: 600,
    color: "#243567",
    letterSpacing: ".04em",
    background: "#f6f9ff"
};

const errStyle = { color: "#dd362b", fontWeight: 600, fontSize: 13, marginTop: 2 };

// ====== Components & Styles ======

function Modal({ onClose, title, children }) {
    const isMobile = window.innerWidth < 768;
    return (
        <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="modalTitle"
            style={{
                position: "fixed", top: 0, left: 0, right: 0, bottom: 0,
                backgroundColor: "rgba(0,0,0,0.15)",
                display: "flex", alignItems: "center", justifyContent: "center", zIndex: 10001,
                padding: isMobile ? "10px" : "0",
            }}
            onClick={onClose}
        >
            <div
                onClick={e => e.stopPropagation()}
                className="modal-responsive"
                style={{
                    background: "#f9fcfe",
                    borderRadius: 15,
                    position: "relative",
                    padding: isMobile ? "20px 15px" : "39px 39px 23px 39px",
                    minWidth: isMobile ? "auto" : 350,
                    maxWidth: isMobile ? "100%" : 490,
                    width: isMobile ? "100%" : "99vw",
                    maxHeight: isMobile ? "90vh" : "auto",
                    overflowY: isMobile ? "auto" : "visible",
                    boxShadow: "0 10px 40px #a5b4fc33"
                }}
            >
                <h3 id="modalTitle" style={{ fontWeight: 800, color: "#215ea7", margin: 0, marginBottom: 18, fontSize: 22, letterSpacing: ".02em" }}>
                    {title}
                </h3>
                {children}
                <button
                    aria-label="Close"
                    onClick={onClose}
                    style={{
                        position: "absolute", top: 14, right: 14, background: "transparent",
                        border: "none", fontSize: 23, cursor: "pointer", color: "#666", userSelect: "none",
                    }}
                >
                    <AiOutlineClose />
                </button>
            </div>
        </div>
    );
}

function SuccessModal({ message, onClose }) {
    return (
        <div
            role="alertdialog"
            aria-modal="true"
            onClick={onClose}
            style={{
                position: "fixed", top: 0, left: 0, right: 0, bottom: 0,
                backgroundColor: "rgba(0,0,0,0.13)", zIndex: 10002,
                display: "flex", justifyContent: "center", alignItems: "center"
            }}>
            <div
                onClick={e => e.stopPropagation()}
                style={{
                    background: "#fafff9",
                    padding: "36px 30px 26px 30px",
                    borderRadius: 16,
                    textAlign: "center",
                    boxShadow: "0 8px 36px #53ec8860",
                    color: "#11602c"
                }}>
                <AiOutlineCheckCircle
                    size={62}
                    color="#17bb5f"
                    style={{ marginBottom: 13, animation: "pop-tick .33s cubic-bezier(.19,.7,.68,1.38)" }}
                />
                <div style={{ fontWeight: 800, fontSize: 21, marginBottom: 2 }}>{message}</div>
                <button onClick={onClose} style={modalBtn("#17bb5f", "#fff", 17, 10)}>Close</button>
                <style>{`
                    @keyframes pop-tick {
                        0% { opacity: 0; transform: scale(0.46);}
                        67% { opacity: 1; transform: scale(1.14);}
                        100% { opacity: 1; transform: scale(1);}
                    }
                `}</style>
            </div>
        </div>
    );
}

